import { Box, Button, CardActions, Stack } from "@mui/material";

import { CbhCustomChatMessage } from "@src/appV2/Chat/CbhCustomChatMessage";
import { ChatChannel } from "../chat.types";
import { CoreMessageType } from "@sendbird/uikit-react/types/utils";
import { Li, Text, Ul } from "@src/lib/ui-react";
import { getUserType } from "@src/appV2/PlacementCandidates/utils/helper";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { getCandidateUrlPath } from "@src/appV2/PlacementCandidates/utils";
import { PLACEMENT_CANDIDATES_LINK_SOURCE_EVENTS } from "@src/appV2/PlacementCandidates/utils";
import {
  transformResponseIntoPlacementApplication,
  canFetchPlacementApplications,
  useFetchPaginatedPlacementApplications,
} from "@src/appV2/PlacementApplications/api/useFetchPaginatedPlacementApplications";
import { isDefined } from "@clipboard-health/util-ts";
import { useIsScheduleInterviewByWorkplace } from "@src/appV2/PlacementApplications/useIsScheduleInterviewByWorkplace";
import { format, utcToZonedTime } from "date-fns-tz";
import { useManagedWorkplaces } from "@src/appV2/PlacementCandidates/hooks/useManagedWorkplaces";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";

interface PlacementApplicationMessageProps {
  message: CoreMessageType;
  currentChannel: ChatChannel;
  channelUrl: string;
}

export function PlacementApplicationMessage({
  message,
  currentChannel,
  channelUrl,
}: PlacementApplicationMessageProps) {
  const { admin, profile, user } = useSession();
  const { isEnabled: isPlacementsEnabled } = useGetPlacementsAccessLevel();
  const [workplaceId, workerId, placementId] = channelUrl.split("_") as Array<string | undefined>;
  const { placementCandidateAccessId, managingFacilityId } = currentChannel.metadata ?? {};
  const chatMessage = message as CoreMessageType & { message: string };

  const isScheduleInterviewByWorkplace = useIsScheduleInterviewByWorkplace();

  const { workplaces } = useManagedWorkplaces();

  const { data: placementApplicationsData } = useFetchPaginatedPlacementApplications(
    {
      workplaceId: profile.userId,
      queryParams: {
        filter: {
          "placement.id": placementId,
          "worker.id": workerId,
          "workplace.id": workplaces.map((workplace) => workplace.id).join(","),
        },
      },
    },
    {
      enabled:
        isDefined(profile) &&
        isPlacementsEnabled &&
        canFetchPlacementApplications(user?.access ?? []),
    }
  );

  if (!chatMessage.message) {
    return undefined as unknown as React.ReactElement;
  }
  const { hcpName, placementTitle, facilityName } = currentChannel.metadata ?? {};

  const placementApplications = placementApplicationsData?.pages.flatMap(
    (placementApplicationsPage) =>
      placementApplicationsPage.data.map((placementApplication) =>
        transformResponseIntoPlacementApplication(
          placementApplication,
          placementApplicationsPage.included
        )
      )
  );
  const placementApplication = placementApplications?.at(0);

  const eventContext = {
    workplaceId,
    managingWorkplaceId: managingFacilityId,
    workerId,
    placementId,
    userId: user?._id,
    userType: getUserType(profile, admin),
    userEmail: user?.email,
    source: "chatBody",
  };

  const placementApplicationMessage = isScheduleInterviewByWorkplace
    ? `${[hcpName, placementApplication?.placement.workerTypes?.[0]]
        .filter(Boolean)
        .join(", ")} is interested in the ${placementTitle} opening at ${facilityName}`
    : `${hcpName} has applied for the ${placementTitle} opening at ${facilityName}. View their candidate profile to check their resume, license and pre-employment credentials.`;

  const messageToDisplay =
    !hcpName || !placementTitle || !facilityName
      ? chatMessage.message
      : placementApplicationMessage;

  const shouldShowScheduleInterviewMessage =
    isScheduleInterviewByWorkplace &&
    placementApplication?.workerInterviewRequest?.dates &&
    placementApplication.workerInterviewRequest.dates.length > 0;

  return (
    <CbhCustomChatMessage
      message={messageToDisplay}
      avatarSxProps={{ justifyContent: "flex-start" }}
      messageContainerSxProps={{ width: "50%" }}
    >
      <CardActions>
        <Stack spacing={1} alignItems="flex-start">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {placementCandidateAccessId &&
              (admin || canFetchPlacementApplications(user?.access ?? [])) && (
                <Button
                  onClick={() => {
                    logEvent(APP_V2_USER_EVENTS.PLACEMENT_CANDIDATE_CHAT_ACTION, {
                      ...eventContext,
                      action: "viewProfileButtonClick",
                    });
                    window.open(
                      getCandidateUrlPath(
                        placementCandidateAccessId,
                        profile?.userId,
                        PLACEMENT_CANDIDATES_LINK_SOURCE_EVENTS.PLACEMENT_CANDIDATE_CHAT_MESSAGE
                      ),
                      "_blank"
                    );
                  }}
                  variant="contained"
                >
                  View profile
                </Button>
              )}
          </Stack>
          <Box>
            <Text>
              {shouldShowScheduleInterviewMessage
                ? "Use this channel to message with the candidate and schedule an interview. Here are their available dates and times for an interview:"
                : "You can use this channel to speak to the candidate directly and invite them to an interview."}
            </Text>
          </Box>

          {shouldShowScheduleInterviewMessage && (
            <Stack direction="column" gap={1}>
              <Ul>
                {placementApplication?.workerInterviewRequest?.dates
                  .map((date) => {
                    const dateInTmz = utcToZonedTime(date, profile.tmz ?? "America/Los_Angeles");
                    return format(dateInTmz, "E, MMMM do");
                  })
                  .map((date) => (
                    <Li key={date}>{date}</Li>
                  ))}
              </Ul>
              {placementApplication?.workerInterviewRequest?.notes && (
                <Text>Notes: {placementApplication.workerInterviewRequest.notes}</Text>
              )}
              {shouldShowScheduleInterviewMessage && (
                <Text>
                  If these don't work, you can suggest a different day and time. Once you've agreed
                  on an interview, click the "Schedule Interview" button above to schedule!
                </Text>
              )}
            </Stack>
          )}
        </Stack>
      </CardActions>
    </CbhCustomChatMessage>
  );
}
